/**
 * Dev-sync J4 — the Checkpoint-4 cascade commit helper.
 *
 * Port of legacy `static/mvc/cascade/cascade_warning.js` guardSelect (90
 * lines, of which this is the ~15 that do anything — survey §B1). Its own
 * docstring (:5-11): the historical cascade-impact confirmation popup "is
 * gone... So this just commits." ★★ THERE IS NO PREVIEW UI TO PORT — every
 * legacy call site still passes `title`/`headline`/`onCancel` and none are
 * read (`onCancel` is never invoked; `?preview=true` has zero frontend
 * consumers). Do not add a confirmation dialog here.
 *
 * Behavior (cascade_warning.js:51-85):
 *  - POST JSON (CSRF) to one of the 8 select/commit_air_conditions
 *    endpoints (urls.py — all accept the resource's own PATCH body verbatim
 *    and return `{updated, cascade: {upstream: [], downstream: []}}`).
 *  - onCommit(resp) runs FIRST, then the promise resolves.
 *  - ★ autoRefresh is opt-OUT (legacy `opts.autoReload !== false`): a NON-EMPTY
 *    cascade re-reads what the cascade changed. Legacy did that 150ms later
 *    with `window.location.reload()` (:73-77) and its own comment says why —
 *    "targeted per-component refresh would be ideal, but every affected
 *    component has its own Backbone view and model collection… for now, force
 *    a page reload". React has the targeted refresh legacy wanted, so this
 *    calls `refreshAfterCascade` instead; see cascadeRefresh.ts for what it
 *    re-reads and why that is the whole set. The opt-OUT and its three callers
 *    are unchanged (the plenum / evap-pack / UDA manual-edit paths update
 *    themselves in place — section.js:226, component_model.js:180).
 *  - on failure: onError(xhr-ish) if provided. Legacy rejects a deferred no
 *    /select caller ever observes (silent total loss, §1 of the legacy
 *    survey); here the promise resolves null instead of rejecting so an
 *    unobserved failure can't surface as an unhandled rejection —
 *    observable behavior identical.
 */

import { getCookie } from 'helpers/index';
import { track } from 'app/analytics';
import { refreshAfterCascade, snapshotSectionLengths } from './cascadeRefresh';

export interface CascadeResponse {
  updated?: Record<string, unknown>;
  cascade?: { upstream?: unknown[]; downstream?: unknown[] };
  [key: string]: unknown;
}

export interface GuardSelectOpts {
  url: string;
  payload?: Record<string, unknown>;
  onCommit?: (resp: CascadeResponse) => void;
  onError?: (resp: { status: number; body: unknown } | null) => void;
  /** opt-OUT (legacy `opts.autoReload !== false`) — a non-empty cascade
   * re-reads the rows it changed unless this is `false`. */
  autoRefresh?: boolean;
  /** Set only by a selector's Select (e.g. 'fan'): sends component_selected
   * once the select succeeds. Other guardSelect writes leave it unset. */
  analyticsComponent?: string;
}

export async function guardSelect(opts: GuardSelectOpts): Promise<CascadeResponse | null> {
  const token = getCookie('csrftoken');
  // ★ BEFORE the POST, synchronously: the last moment the section-list cache
  // is guaranteed to predate every write this request makes. The refresh
  // compares against it to decide whether the Select resized its host section
  // — it cannot re-read that baseline later, because `onCommit`'s own section
  // PATCH merges the server's full row (new `length` included) into the same
  // cache. See cascadeRefresh.ts's header.
  const sectionLengths = snapshotSectionLengths();
  let resp: Response;
  try {
    resp = await fetch(opts.url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(token ? { 'X-CSRFToken': token } : {}),
      },
      body: JSON.stringify(opts.payload || {}),
    });
  } catch {
    opts.onError?.(null);
    return null;
  }
  if (!resp.ok) {
    let body: unknown = null;
    try {
      body = await resp.json();
    } catch {
      /* non-JSON error body */
    }
    opts.onError?.({ status: resp.status, body });
    return null;
  }
  let data: CascadeResponse = {};
  try {
    data = (await resp.json()) as CascadeResponse;
  } catch {
    /* 204-ish */
  }
  // Before the cascade refresh below, so the event is queued first.
  if (opts.analyticsComponent) track('component_selected', { component: opts.analyticsComponent });
  opts.onCommit?.(data);
  const cascade = data && data.cascade;
  const hadCascade =
    !!cascade &&
    ((cascade.upstream ? cascade.upstream.length > 0 : false) ||
      (cascade.downstream ? cascade.downstream.length > 0 : false));
  if (hadCascade && opts.autoRefresh !== false) {
    // Legacy's 150 ms wait was there so the reload could not race the writes
    // `onCommit` had just issued, and it is kept for the same reason — but a
    // timer is NOT an ordering guarantee: nothing awaits those writes, so a
    // re-read fired 150 ms later can still overtake one and read the row back
    // as it was. `refreshAfterCascade` waits for the section-patch queue to
    // drain and for each cache entry to be idle before it asks; the delay here
    // only keeps the common case out of that wait.
    window.setTimeout(() => {
      try {
        refreshAfterCascade(cascade, sectionLengths);
      } catch {
        /* a refresh must never break the Select that triggered it */
      }
    }, 150);
  }
  return data;
}
